import { ApiError } from '@/lib/api'
import type {
  CustomFieldConversionPreview,
  CustomFieldNarrowingWarning,
} from '@/lib/contacts/types'

export function customFieldNarrowingWarning(error: unknown) {
  if (!(error instanceof ApiError) || error.status !== 409) return null

  const body = error.body
  if (typeof body !== 'object' || body === null || !('narrowing_warning' in body)) return null

  const warning = (body as { narrowing_warning: unknown }).narrowing_warning
  if (!isCustomFieldNarrowingWarning(warning)) return null

  return warning
}

export function customFieldConversionPreview(error: unknown) {
  if (!(error instanceof ApiError) || error.status !== 409) return null

  const body = error.body
  if (typeof body !== 'object' || body === null || !('conversion_preview' in body)) return null

  const preview = (body as { conversion_preview: unknown }).conversion_preview
  if (!isCustomFieldConversionPreview(preview)) return null

  return preview
}

export function isCustomFieldNarrowingWarning(
  value: unknown,
): value is CustomFieldNarrowingWarning {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as CustomFieldNarrowingWarning).deleted_value_count === 'number' &&
    typeof (value as CustomFieldNarrowingWarning).excluded_contact_type === 'string'
  )
}

export function isCustomFieldConversionPreview(
  value: unknown,
): value is CustomFieldConversionPreview {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as CustomFieldConversionPreview).from_field_type === 'string' &&
    typeof (value as CustomFieldConversionPreview).to_field_type === 'string' &&
    typeof (value as CustomFieldConversionPreview).cleared_value_count === 'number' &&
    Array.isArray((value as CustomFieldConversionPreview).value_mappings)
  )
}

export function nonNarrowingError(error: Error | null) {
  return customFieldNarrowingWarning(error) ? null : error
}

export function nonConversionPreviewError(error: Error | null) {
  return customFieldConversionPreview(error) ? null : error
}

export function affiliationMutationMessage(error: Error) {
  const duplicate = apiFieldMessages(error, 'base').find((message) =>
    message.includes('already exists'),
  )
  if (duplicate) return duplicate

  const startDate = apiFieldMessages(error, 'started_on')[0]
  if (startDate) return `Start date ${startDate}`

  return error.message
}

export function personEmailError(error: Error | null) {
  const message = apiFieldMessages(error, 'email')[0]
  if (!message) return undefined
  if (message.includes('already in use')) return 'Already in use by another contact.'

  return message
}

export function contactNameError(error: Error | null) {
  return apiFieldMessages(error, 'name')[0]
}

export function customFieldValueError(error: Error | null) {
  return apiFieldMessages(error, 'custom_field_values')[0]
}

export function apiFieldMessages(error: Error | null, field: string) {
  if (!(error instanceof ApiError)) return []
  if (!hasFieldErrors(error.body)) return []

  return error.body.errors[field] ?? []
}

export function hasFieldErrors(body: unknown): body is { errors: Record<string, string[]> } {
  if (typeof body !== 'object' || body === null || !('errors' in body)) return false

  const errors = (body as { errors: unknown }).errors
  return typeof errors === 'object' && errors !== null
}

export function apiMessage(error: Error) {
  if (error instanceof ApiError) return error.message

  return 'Sign-in failed.'
}
